import { useState, useEffect, useMemo } from 'react';
import { tableBookingService } from '@/features/booking/services/tableBookingService';
import { formatDateString } from '@/lib/utils';
import type { 
  EventSchedule, 
  TableAvailabilitySlot,
  TableBookingState 
} from '@/features/booking/types/tableBookingTypes';

export const useTableBooking = (eventId: string) => {
  const [state, setState] = useState<TableBookingState>({
    eventSchedules: [],
    selectedEventSchedule: null,
    tableAvailability: {},
    selectedDate: null,
    selectedBookings: {},
    allTablesInVenue: [],
    bookingCategories: new Set(),
    loading: true,
    error: null,
  });

  
  useEffect(() => {
    const loadEventSchedules = async () => {
      try {
        setState(prev => ({ ...prev, loading: true, error: null }));
        const schedules = await tableBookingService.getEventSchedules(eventId);
        
        const categories = new Set<string>();
        schedules.forEach(schedule => {
          if (schedule.event_booking_categories) {
            schedule.event_booking_categories.forEach(category => {
              if (category.booking_category.is_table_booking) {
                categories.add(category.booking_category.name);
              }
            });
          }
        });

        let allTables: Array<{ number: string; size: number }> = [];
        if (schedules.length > 0) {
          const firstSchedule = schedules[0];
          if (firstSchedule.event_booking_categories) {
            const tableBookingCategories = firstSchedule.event_booking_categories.filter(
              cat => cat.booking_category.is_table_booking
            );
            
            if (tableBookingCategories.length > 0) {
              const tableCategories = tableBookingCategories[0].booking_category.table_categories;
              if (tableCategories) {
                allTables = tableCategories.flatMap(cat => 
                  cat.table_numbers.map(num => ({ number: num, size: cat.table_size }))
                );
              }
            }
          }
        }

        setState(prev => ({
          ...prev,
          eventSchedules: schedules,
          selectedEventSchedule: schedules[0] || null,
          allTablesInVenue: allTables,
          bookingCategories: categories,
          loading: false,
        }));
      } catch (error) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to load event schedules',
        }));
      }
    };

    if (eventId) {
      loadEventSchedules();
    }
  }, [eventId]);

  useEffect(() => {
    const loadTableAvailability = async () => {
      if (!state.selectedEventSchedule) return;

      try {
        // Don't set loading to true here to avoid double loading
        const availability = await tableBookingService.getTableAvailability(
          state.selectedEventSchedule.id
        );
        
        setState(prev => ({
          ...prev,
          tableAvailability: availability,
        }));
      } catch (error) {
        setState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Failed to load table availability',
        }));
      }
    };

    loadTableAvailability();
  }, [state.selectedEventSchedule]);

  // Computed values
  const availableDates = useMemo(() => {
    return state.eventSchedules.map(schedule => 
      formatDateString(new Date(schedule.start_time), 'YYYY-MM-DD')
    );
  }, [state.eventSchedules]);

  const totalPrice = useMemo(() => {
    return Object.values(state.selectedBookings).reduce(
      (sum, slot) => sum + slot.event_booking_category.price, 
      0
    );
  }, [state.selectedBookings]);

  // Actions
  const selectEventSchedule = (schedule: EventSchedule) => {
    setState(prev => ({
      ...prev,
      selectedEventSchedule: schedule,
      selectedBookings: {}, // Reset selections when schedule changes
    }));
  };

  const selectDate = (date: string) => {
    setState(prev => ({
      ...prev,
      selectedDate: date,
      selectedBookings: {}, // Reset selections when date changes
    }));
  };

  const addBooking = (tableNumber: string, slot: TableAvailabilitySlot) => {
    setState(prev => ({
      ...prev,
      selectedBookings: {
        ...prev.selectedBookings,
        [tableNumber]: slot,
      },
    }));
  };

  const removeBooking = (tableNumber: string) => {
    setState(prev => {
      const newBookings = { ...prev.selectedBookings };
      delete newBookings[tableNumber];
      return {
        ...prev,
        selectedBookings: newBookings,
      };
    });
  };

  const clearAllBookings = () => {
    setState(prev => ({
      ...prev,
      selectedBookings: {},
    }));
  };

  const createBooking = async () => {
    if (Object.keys(state.selectedBookings).length === 0) {
      throw new Error('Please select at least one table to book');
    }

    const bookingPromises = Object.entries(state.selectedBookings).map(([tableNumber, slot]) =>
      tableBookingService.createTableBooking({
        eventScheduleId: state.selectedEventSchedule!.id,
        tableNumber,
        startTime: slot.start_time,
        eventBookingCategoryId: slot.event_booking_category.id,
      })
    );

    const results = await Promise.all(bookingPromises);
    return results;
  };

  return {
    ...state,
    availableDates,
    totalPrice,
    selectEventSchedule,
    selectDate,
    addBooking,
    removeBooking,
    clearAllBookings,
    createBooking,
  };
};

